/**
 * DeepLinkLanding — Smart landing page & web fallback for shared profile and group links.
 *
 * Handling:
 * 1. Mobile (Android/iOS):
 *    - Tries to open the installed app via custom scheme `weightlossbuddy://${type}/${id}`.
 *    - If app is NOT installed (page remains active after timeout), redirects to the appropriate store.
 *    - Offers explicit "Open in App", Store, and "Continue on Web" actions.
 * 2. Desktop:
 *    - Displays web fallback card with direct navigation into the web app or app store links.
 * 3. Invalid/missing links:
 *    - Displays a user-friendly error card without crashing.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  buildCustomSchemeUrl,
  parseDeepLink,
  type DeepLinkTarget,
} from '@wlb/shared/utils/deepLinking';
import { BrandLogo } from '../components/ui/BrandLogo';
import './DeepLinkLanding.css';

interface DeepLinkLandingProps {
  forcedType?: 'profile' | 'group' | 'post';
}

const detectDevice = (): { isAndroid: boolean; isIOS: boolean; isMobile: boolean } => {
  if (typeof navigator === 'undefined') {
    return { isAndroid: false, isIOS: false, isMobile: false };
  }
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isMobile = isAndroid || isIOS;
  return { isAndroid, isIOS, isMobile };
};

export function DeepLinkLanding({ forcedType }: DeepLinkLandingProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ userId?: string; groupId?: string; postId?: string; userGuid?: string }>();
  // Ref, not state: flipping a state flag here would rerun this effect, run its
  // cleanup (clearTimeout + listener removal) and cancel the store fallback.
  const autoRedirectAttemptedRef = useRef(false);

  const device = useMemo(() => detectDevice(), []);

  // Resolve target from route params or full URL
  const target: DeepLinkTarget | null = useMemo(() => {
    const rawId = params.userId || params.userGuid || params.groupId || params.postId;
    if (forcedType && rawId) {
      if (forcedType === 'profile') return { type: 'profile', userId: rawId };
      if (forcedType === 'group') return { type: 'group', groupId: rawId };
      if (forcedType === 'post') return { type: 'post', postId: rawId };
    }

    if (typeof window !== 'undefined') {
      const parsed = parseDeepLink(window.location.href);
      if (parsed) return parsed;
    }

    const path = location.pathname.toLowerCase();
    if (params.userId || path.startsWith('/profile') || path.startsWith('/u/')) {
      const id = params.userId || params.userGuid || path.split('/').filter(Boolean).pop();
      if (id && id !== 'profile' && id !== 'u') {
        return { type: 'profile', userId: id };
      }
    }
    if (params.groupId || path.startsWith('/group')) {
      const id = params.groupId || path.split('/').filter(Boolean).pop();
      if (id && id !== 'group' && id !== 'groups') {
        return { type: 'group', groupId: id };
      }
    }
    if (params.postId || path.startsWith('/post')) {
      const id = params.postId || path.split('/').filter(Boolean).pop();
      if (id && id !== 'post') {
        return { type: 'post', postId: id };
      }
    }

    return null;
  }, [forcedType, params, location.pathname]);

  const schemeUrl = useMemo(() => (target ? buildCustomSchemeUrl(target) : ''), [target]);
  const storeUrl = useMemo(() => {
    if (device.isIOS) return APP_STORE_URL;
    return PLAY_STORE_URL;
  }, [device.isIOS]);

  const webDestination = useMemo(() => {
    if (!target) return '/';
    if (target.type === 'profile') return `/profile/others/${encodeURIComponent(target.userId)}`;
    if (target.type === 'group') return `/groups/${encodeURIComponent(target.groupId)}`;
    return '/';
  }, [target]);

  // Handle Mobile Automatic App Launch attempt
  useEffect(() => {
    if (!device.isMobile || !target || autoRedirectAttemptedRef.current) {
      return;
    }
    autoRedirectAttemptedRef.current = true;

    // Attempt to launch the native app via custom scheme without force-redirecting to store
    if (schemeUrl) {
      window.location.href = schemeUrl;
    }
  }, [device.isMobile, target, schemeUrl]);

  // Handle Desktop Authenticated Direct Navigation
  useEffect(() => {
    if (!device.isMobile && isAuthenticated && target) {
      navigate(webDestination, { replace: true });
    }
  }, [device.isMobile, isAuthenticated, target, webDestination, navigate]);

  const handleContinueOnWeb = () => {
    if (isAuthenticated) {
      navigate(webDestination);
    } else {
      navigate('/login', { state: { from: webDestination } });
    }
  };

  if (!target) {
    return (
      <main className="deep-link-landing">
        <div className="deep-link-landing__card" role="region" aria-label={t('deepLinkLanding.invalidLinkTitle')}>
          <div className="deep-link-landing__logo-wrap">
            <BrandLogo />
          </div>
          <h1 className="deep-link-landing__title">{t('deepLinkLanding.invalidLinkTitle')}</h1>
          <p className="deep-link-landing__subtitle">{t('deepLinkLanding.invalidLinkSubtitle')}</p>
          <div className="deep-link-landing__actions">
            <button
              type="button"
              className="deep-link-landing__btn deep-link-landing__btn--primary"
              onClick={() => navigate('/')}
            >
              {t('deepLinkLanding.backToHome')}
            </button>
          </div>
        </div>
      </main>
    );
  }

  const title =
    target.type === 'profile'
      ? t('deepLinkLanding.profileTitle')
      : target.type === 'group'
      ? t('deepLinkLanding.groupTitle')
      : t('deepLinkLanding.postTitle');

  const subtitle =
    target.type === 'profile'
      ? t('deepLinkLanding.profileSubtitle')
      : target.type === 'group'
      ? t('deepLinkLanding.groupSubtitle')
      : t('deepLinkLanding.postSubtitle');

  return (
    <main className="deep-link-landing">
      <div className="deep-link-landing__card" role="region" aria-label={title}>
        <div className="deep-link-landing__logo-wrap">
          <BrandLogo />
        </div>

        <div className="deep-link-landing__icon-badge" aria-hidden="true">
          <svg className="deep-link-landing__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {target.type === 'profile' && (
              <>
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </>
            )}
            {target.type === 'group' && (
              <>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </>
            )}
            {target.type === 'post' && (
              <>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </>
            )}
          </svg>
        </div>

        <h1 className="deep-link-landing__title">{title}</h1>
        <p className="deep-link-landing__subtitle">{subtitle}</p>

        <div className="deep-link-landing__actions">
          {device.isMobile ? (
            <>
              {schemeUrl && (
                <a
                  href={schemeUrl}
                  className="deep-link-landing__btn deep-link-landing__btn--primary"
                >
                  {t('deepLinkLanding.openInApp')}
                </a>
              )}
              {storeUrl && (
                <a
                  href={storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="deep-link-landing__btn deep-link-landing__btn--store"
                >
                  {device.isIOS ? t('deepLinkLanding.downloadOnAppStore') : t('deepLinkLanding.getOnGooglePlay')}
                </a>
              )}
              <button
                type="button"
                className="deep-link-landing__btn deep-link-landing__btn--tertiary"
                onClick={handleContinueOnWeb}
              >
                {t('deepLinkLanding.continueOnWeb')}
              </button>
            </>
          ) : (
            <>
              {isAuthenticated ? (
                <button
                  type="button"
                  className="deep-link-landing__btn deep-link-landing__btn--primary"
                  onClick={() => navigate(webDestination)}
                >
                  {t('deepLinkLanding.continueOnWeb')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="deep-link-landing__btn deep-link-landing__btn--primary"
                    onClick={() => navigate('/login', { state: { from: webDestination } })}
                  >
                    {t('deepLinkLanding.logIn')}
                  </button>
                  <button
                    type="button"
                    className="deep-link-landing__btn deep-link-landing__btn--secondary"
                    onClick={() => navigate('/signup')}
                  >
                    {t('deepLinkLanding.signUp')}
                  </button>
                </>
              )}

              <div className="deep-link-landing__store-group">
                <p className="deep-link-landing__store-title">{t('deepLinkLanding.downloadApp')}</p>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="deep-link-landing__btn deep-link-landing__btn--store"
                >
                  {t('deepLinkLanding.getOnGooglePlay')}
                </a>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="deep-link-landing__btn deep-link-landing__btn--store"
                >
                  {t('deepLinkLanding.downloadOnAppStore')}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
